import { useState, type FormEvent } from 'react';
import { api, type AuditTarget } from '../api';
import { formatDateTime } from '../format';
import { useLoader } from '../hooks';
import { Alert } from './Alert';
import { Field } from './Field';

export const AUDIT_TARGET_TYPES: { value: string; label: string; hint: string }[] = [
  { value: 'instance', label: 'Instance', hint: "identifiant de l'instance (UUID)" },
  { value: 'plugin', label: 'Plugin', hint: 'identifiant du plugin (UUID)' },
  { value: 'skill-pack', label: 'Pack de skills', hint: 'identifiant du pack (UUID)' },
  { value: 'flag', label: 'Flag', hint: 'clé du flag' },
  { value: 'job', label: 'Tâche', hint: 'identifiant de la tâche (UUID)' },
];

/**
 * Journal d'audit. Sans `fixedTarget`, un filtre « type de cible + identifiant » est proposé (le serveur n'applique le filtre que
 * si les deux sont renseignés). Avec `fixedTarget`, le journal est restreint à cette cible et le filtre est masqué.
 */
export function AuditTab({ fixedTarget }: { fixedTarget?: AuditTarget }) {
  const [applied, setApplied] = useState<AuditTarget | null>(null);
  const [type, setType] = useState('');
  const [targetId, setTargetId] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const target = fixedTarget ?? applied;
  const {
    data: entries,
    error,
    reload,
  } = useLoader(() => api.audit(100, target ?? undefined), target ? `${target.type}:${target.id}` : '');

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!type && !targetId.trim()) {
      setApplied(null);
      setFormError(null);
      return;
    }
    if (!type || !targetId.trim()) {
      setFormError('Renseignez le type de cible et son identifiant, ou videz les deux champs.');
      return;
    }
    setFormError(null);
    setApplied({ type, id: targetId.trim() });
  }

  function clear() {
    setType('');
    setTargetId('');
    setFormError(null);
    setApplied(null);
  }

  const hint = AUDIT_TARGET_TYPES.find((t) => t.value === type)?.hint;

  return (
    <div className="stack">
      {!fixedTarget && (
        <form className="inline-form" onSubmit={submit} aria-label="Filtrer le journal par cible" noValidate>
          <Field label="Type de cible">
            {(p) => (
              <select {...p} value={type} onChange={(e) => setType(e.target.value)}>
                <option value="">Toutes les cibles</option>
                {AUDIT_TARGET_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Identifiant de la cible" hint={hint} error={formError}>
            {(p) => (
              <input
                {...p}
                type="text"
                value={targetId}
                autoComplete="off"
                spellCheck={false}
                onChange={(e) => setTargetId(e.target.value)}
              />
            )}
          </Field>
          <button type="submit" className="btn">
            Filtrer
          </button>
          {applied && (
            <button type="button" className="btn" onClick={clear}>
              Effacer le filtre
            </button>
          )}
        </form>
      )}
      <div className="row-actions">
        <button type="button" className="btn btn-small" onClick={() => void reload()}>
          Actualiser
        </button>
        {target && (
          <span className="muted">
            Cible : <code>{target.type}</code> · <code>{target.id}</code>
          </span>
        )}
      </div>
      {error && <Alert>{error}</Alert>}
      {entries === null && !error && (
        <p className="muted" aria-busy="true">
          Chargement du journal…
        </p>
      )}
      {entries?.length === 0 && (
        <p className="muted">{target ? 'Aucune entrée pour cette cible.' : "Le journal d'audit est vide."}</p>
      )}
      {entries && entries.length > 0 && (
        <div className="table-wrap">
          <table>
            <caption className="sr-only">Journal d'audit</caption>
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col">Acteur</th>
                <th scope="col">Action</th>
                <th scope="col">Cible</th>
                <th scope="col">Détails</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id}>
                  <td className="nowrap">{formatDateTime(e.at)}</td>
                  <td>{e.actorUsername ?? '—'}</td>
                  <td>
                    <code>{e.action}</code>
                  </td>
                  <td>{e.targetType ? `${e.targetType}${e.targetId ? ` · ${e.targetId}` : ''}` : '—'}</td>
                  <td>
                    {Object.keys(e.details ?? {}).length > 0 ? (
                      <code className="details">{JSON.stringify(e.details)}</code>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
